import {defineStore} from 'pinia'
import {reactive} from 'vue'
import {Capacitor} from '@capacitor/core'
import {Preferences} from '@capacitor/preferences'
import {
  getItem as idbGetItem,
  setItem as idbSetItem,
  removeItem as idbRemoveItem,
  keys as idbKeys,
} from 'localforage'
import UniversalCookie from 'universal-cookie'
import {LocalPreferenceType} from './enum/LocalPreferenceType'

const KEY_PREFIXES = Object.values(LocalPreferenceType) as string[]
const MIGRATION_MARKER_KEY = '__local-preference-migrated__'

// On a native Capacitor container the web storage APIs (IndexedDB/localStorage) can be
// evicted by the OS under storage pressure, so persist through the native Preferences
// API (UserDefaults / SharedPreferences). On the web there is no native layer, so use
// IndexedDB (localforage).
const isNativePlatform = Capacitor.isNativePlatform()

function isManagedKey(key: string): boolean {
  return KEY_PREFIXES.some(prefix => key === prefix || key.startsWith(`${prefix}-`))
}

function buildKey(type: LocalPreferenceType, suffix?: string | null): string {
  return suffix != null ? `${type}-${suffix}` : type
}

async function persistGet(key: string): Promise<string | null> {
  if (isNativePlatform) {
    return (await Preferences.get({key})).value
  }
  return (await idbGetItem<string>(key)) ?? null
}

async function persistSet(key: string, value: string): Promise<void> {
  if (isNativePlatform) {
    await Preferences.set({
      key,
      value,
    })
    return
  }
  await idbSetItem(key, value)
}

async function persistRemove(key: string): Promise<void> {
  if (isNativePlatform) {
    await Preferences.remove({key})
    return
  }
  await idbRemoveItem(key)
}

async function persistKeys(): Promise<string[]> {
  if (isNativePlatform) {
    return (await Preferences.keys()).keys
  }
  return idbKeys()
}

export const useLocalPreferenceStore = defineStore('localPreference', () => {
  const cache = reactive(new Map<string, string>())
  let hydrationPromise: Promise<void> | null = null

  function get(type: LocalPreferenceType, suffix?: string | null): string | undefined {
    return cache.get(buildKey(type, suffix))
  }

  async function set(type: LocalPreferenceType, value: string, suffix?: string | null): Promise<void> {
    const key = buildKey(type, suffix)
    cache.set(key, value)
    try {
      await persistSet(key, value)
    } catch {
      // ignore persistence errors; cache still holds the value for this session
    }
  }

  async function remove(type: LocalPreferenceType, suffix?: string | null): Promise<void> {
    const key = buildKey(type, suffix)
    cache.delete(key)
    try {
      await persistRemove(key)
    } catch {
      // ignore
    }
  }

  async function clearAll(): Promise<void> {
    cache.clear()
    try {
      const keys = await persistKeys()
      await Promise.all(keys.filter(isManagedKey).map(async key => {
        try {
          await persistRemove(key)
        } catch {
          // ignore
        }
      }))
    } catch {
      // ignore
    }
  }

  function hydrate(): Promise<void> {
    if (!hydrationPromise) {
      hydrationPromise = doHydrate()
    }
    return hydrationPromise
  }

  async function doHydrate(): Promise<void> {
    try {
      const keys = await persistKeys()
      for (const key of keys) {
        if (!isManagedKey(key)) {
          continue
        }
        const value = await persistGet(key)
        if (typeof value === 'string') {
          cache.set(key, value)
        }
      }
    } catch {
      // ignore
    }
    await migrateFromCookiesOnce()
  }

  async function migrateFromCookiesOnce(): Promise<void> {
    try {
      const alreadyMigrated = await persistGet(MIGRATION_MARKER_KEY)
      if (alreadyMigrated) {
        return
      }
      const cookies = new UniversalCookie()
      const all = cookies.getAll<Record<string, unknown>>() ?? {}
      for (const name of Object.keys(all)) {
        if (!isManagedKey(name)) {
          continue
        }
        const value = all[name]
        if (typeof value !== 'string') {
          continue
        }
        if (!cache.has(name)) {
          cache.set(name, value)
        }
        let isPersisted = false
        try {
          await persistSet(name, value)
          isPersisted = true
        } catch {
          // keep the legacy cookie if the value could not be persisted
        }
        if (isPersisted) {
          try {
            cookies.remove(name, {path: '/'})
          } catch {
            // ignore
          }
        }
      }
      await persistSet(MIGRATION_MARKER_KEY, '1')
    } catch {
      // ignore
    }
  }

  return {
    get,
    set,
    remove,
    clearAll,
    hydrate,
  }
})
